import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Button,
  Drawer,
  Divider,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  LocationOn,
  Bed,
  Bathtub,
  SquareFoot,
} from "@mui/icons-material";

const PROPERTY_TYPES = [
  { value: "all", label: "All" },
  { value: "property", label: "Property" },
  { value: "project", label: "Project" },
  { value: "villa", label: "Villa" },
  { value: "apartment", label: "Apartment" },
  { value: "plot", label: "Plot" },
];

const CITIES = ["All Cities", "Mumbai", "Delhi", "Chennai", "Bangalore"];

export default function AllProperties() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [properties, setProperties] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    city: "All Cities",
    type: "all",
    price: [0, 10000000],
  });
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    if (location.state?.properties) {
      setProperties(location.state.properties);
      setFiltered(location.state.properties);
    } else {
      fetchMock();
    }
  }, []);

  useEffect(() => {
    let data = [...properties];

    if (search) {
      data = data.filter(
        (p) =>
          p.name?.toLowerCase().includes(search.toLowerCase()) ||
          p.city?.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (filters.city !== "All Cities") {
      data = data.filter((p) => p.city === filters.city);
    }

    if (filters.type !== "all") {
      data = data.filter((p) => p.type === filters.type);
    }

    data = data.filter(
      (p) =>
        (p.price || 0) >= filters.price[0] &&
        (p.price || 0) <= filters.price[1]
    );

    setFiltered(data);
  }, [search, filters, properties]);

  const fetchMock = async () => {
    setLoading(true);
    setTimeout(() => {
      setProperties(MOCK_DATA);
      setFiltered(MOCK_DATA);
      setLoading(false);
    }, 1000);
  };

  const formatPrice = (p = 0) =>
    p >= 10000000 ? `₹${(p / 10000000).toFixed(1)} Cr` : `₹${(p / 100000).toFixed(1)} L`;

  const getUrl = (p) =>
    ({
      property: `/PropertyDetailsScreen?${p.refferal_code}`,
      project: `/ProjectDetailsScreen?${p.refferal_code}`,
      villa: `/IndividualVillaScreen?${p.refferal_code}`,
      apartment: `/ApartmentPlotsScreen?${p.refferal_code}`,
      plot: `/ApartmentPlotsScreen?${p.refferal_code}`,
    }[p.type]);

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh" }}>
      {/* HEADER */}
      <Box sx={{ p: 2, bgcolor: "white", boxShadow: 1 }}>
        <TextField
          fullWidth
          placeholder="Search properties..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            startIcon={<FilterIcon />}
            variant="outlined"
            onClick={() => setDrawer(true)}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* CONTENT */}
      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {filtered.map((p) => (
              <Grid xs={12} sm={6} md={4} lg={3} key={p.id}>
                <Card
                  onClick={() => navigate(getUrl(p))}
                  sx={{
                    height: 420,
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    overflow: "hidden",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "translateY(-6px)",
                      boxShadow: 6,
                      cursor: "pointer",
                    },
                  }}
                >
                  {/* MEDIA */}
                  <Box sx={{ height: 180, position: "relative" }}>
                    {p.videoUrl ? (
                      <video
                        src={p.videoUrl}
                        muted
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <img
                        src={p.iconUrl}
                        alt={p.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    )}

                    <Chip
                      label={p.type}
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        bgcolor: "white",
                      }}
                    />
                  </Box>

                  {/* DETAILS */}
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography fontWeight="bold" gutterBottom>
                      {p.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      <LocationOn sx={{ fontSize: 14 }} /> {p.city}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                      {p.bhk && (
                        <Typography variant="caption">
                          <Bed sx={{ fontSize: 14 }} /> {p.bhk} BHK
                        </Typography>
                      )}
                      {p.area && (
                        <Typography variant="caption">
                          <SquareFoot sx={{ fontSize: 14 }} /> {p.area} sqft
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ mt: "auto", display: "flex", justifyContent: "space-between" }}>
                      <Typography color="primary" fontWeight="bold">
                        {formatPrice(p.price)}
                      </Typography>
                      <Button size="small" variant="contained">
                        View
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* FILTER DRAWER */}
      <Drawer anchor="right" open={drawer} onClose={() => setDrawer(false)}>
        <Box sx={{ p: 3, width: 300 }}>
          <Typography fontWeight="bold">Filters</Typography>
          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            >
              {CITIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
            >
              {PROPERTY_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography>Price Range</Typography>
          <Slider
            value={filters.price}
            onChange={(_, v) => setFilters({ ...filters, price: v })}
            min={0}
            max={10000000}
          />

          <Button fullWidth variant="contained" onClick={() => setDrawer(false)}>
            Apply
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
}

/* MOCK DATA */
const MOCK_DATA = [
  {
    id: 1,
    name: "Luxury Villa",
    city: "Chennai",
    type: "villa",
    price: 7500000,
    area: 2400,
    bhk: 4,
    iconUrl: "https://images.unsplash.com/photo-1613977257363-707ba9348227",
    refferal_code: "VL001",
  },
  {
    id: 2,
    name: "Modern Apartment",
    city: "Mumbai",
    type: "apartment",
    price: 3200000,
    area: 1200,
    bhk: 2,
    iconUrl: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00",
    refferal_code: "APT002",
  },
];
